/**
 * Boreholes: lithology colours and pure geometry helpers for the map, section and 3D
 * views (docs/BOREHOLES.md). Everything here is unit-tested in lib/boreholes.test.js.
 *
 * LITHOLOGY_COLORS mirrors backend/boreholes/lithology.py; tests/test_boreholes.py
 * fails if the two tables drift apart.
 */

export const UNKNOWN_COLOR = '#c8c8c8';

export const LITHOLOGY_COLORS = {
  'gw': '#e2a840',
  'gp': '#efc46a',
  'gm': '#d39a52',
  'gc': '#c08446',
  'sw': '#f5d76e',
  'sp': '#fae79b',
  'sm': '#e3c86f',
  'sc': '#d2b062',
  'ml': '#c3dba5',
  'cl': '#86bf7f',
  'ol': '#8f7f6b',
  'mh': '#a8c98d',
  'ch': '#5f9f61',
  'oh': '#6f5f50',
  'pt': '#4b3a28',
  'topsoil': '#7a5230',
  'fill': '#9e9e9e',
  'made ground': '#9e9e9e',
  'gravel': '#e2a840',
  'sand': '#f5d76e',
  'silt': '#c3dba5',
  'clay': '#86bf7f',
  'loam': '#a9875c',
  'peat': '#4b3a28',
  'organic': '#6f5f50',
  'till': '#b7a27e',
  'boulders': '#b07c3e',
  'cobbles': '#c48d45',
  'sandstone': '#e0bb67',
  'siltstone': '#b4b585',
  'mudstone': '#7f8873',
  'claystone': '#6f9d72',
  'shale': '#6c7a8a',
  'limestone': '#98c3e0',
  'dolomite': '#b6a8d8',
  'chalk': '#f0f0e2',
  'marl': '#a7c4b0',
  'coal': '#232323',
  'conglomerate': '#cf9c5a',
  'breccia': '#b98a5c',
  'evaporite': '#e8d0e8',
  'gypsum': '#e8d0e8',
  'granite': '#e29aa2',
  'basalt': '#4d4d5a',
  'andesite': '#7a6f7f',
  'gneiss': '#c78fb0',
  'schist': '#9b86a8',
  'slate': '#5a6470',
  'quartzite': '#e6dccd',
  'marble': '#eae6f0',
  'weathered rock': '#c9a27e',
  'saprolite': '#c9a27e',
  'bedrock': '#8a8a8a',
  'rock': '#8a8a8a',
  'water': '#4fa3e0',
  'void': '#ffffff',
};

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** A valid '#rgb'/'#rrggbb' colour (lower case), or null. */
export function normalizeColor(value) {
  if (!value) return null;
  let v = String(value).trim();
  if (!v.startsWith('#')) v = `#${v}`;
  return HEX.test(v) ? v.toLowerCase() : null;
}

/**
 * The table key a free-text lithology resolves to, or null. Same rules as the backend:
 * whole string, then two-word names, then the first known word longer than 2 letters.
 */
export function lithologyKey(lithology) {
  if (!lithology) return null;
  const text = String(lithology).trim().toLowerCase();
  if (LITHOLOGY_COLORS[text]) return text;
  const words = text.match(/[a-z]+/g) || [];
  for (let i = 0; i + 1 < words.length; i++) {
    const pair = `${words[i]} ${words[i + 1]}`;
    if (LITHOLOGY_COLORS[pair]) return pair;
  }
  return words.find(w => w.length > 2 && LITHOLOGY_COLORS[w]) || null;
}

/**
 * Colour for an interval. Order: the user's per-lithology overrides ({ key: '#hex' },
 * keyed like the table or by the raw lithology text), the interval's stored colour, the
 * table, grey.
 */
export function lithologyColor(lithology, stored = null, overrides = {}) {
  const key = lithologyKey(lithology);
  const raw = lithology ? String(lithology).trim().toLowerCase() : '';
  return normalizeColor(overrides[raw]) || normalizeColor(key && overrides[key])
    || normalizeColor(stored) || (key ? LITHOLOGY_COLORS[key] : UNKNOWN_COLOR);
}

// Per-viewer colour overrides ({ lithology: '#hex' }); a convenience, so browser storage.
const OVERRIDES_KEY = 'ugis.lithologyColorOverrides';

export function loadColorOverrides() {
  try {
    const v = JSON.parse(window.localStorage.getItem(OVERRIDES_KEY) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch (e) {
    return {};
  }
}

export function saveColorOverrides(overrides) {
  try {
    window.localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides || {}));
  } catch (e) {
    // storage unavailable (private window): overrides last for this page only
  }
}

// ── Map ─────────────────────────────────────────────────────────────────────

/** 'Topsoil 0-1.5 | Clay 1.5-6 | ...' (first `max` intervals). */
export function lithologySummary(intervals = [], max = 6) {
  if (!intervals?.length) return '';
  const fmt = (v) => Number(Number(v).toFixed(2));
  const parts = intervals.slice(0, max).map(iv => `${iv.lithology || '?'} ${fmt(iv.depth_from)}-${fmt(iv.depth_to)}`);
  if (intervals.length > max) parts.push(`+${intervals.length - max} more`);
  return parts.join(' | ');
}

/** Point features for the map layer (boreholes without a location are skipped). */
export function boreholesToGeoJSON(boreholes = []) {
  return {
    type: 'FeatureCollection',
    features: boreholes
      .filter(b => Number.isFinite(b.lon) && Number.isFinite(b.lat))
      .map(b => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [b.lon, b.lat] },
        properties: {
          id: b.id, name: b.name, elevation: b.elevation, total_depth: b.total_depth,
          lithology: lithologySummary(b.intervals),
        },
      })),
  };
}

/** Borehole symbol: white disc with a thick dark ring. */
export const boreholePaint = () => ({
  'circle-radius': 6,
  'circle-color': '#ffffff',
  'circle-opacity': 0.95,
  'circle-stroke-color': '#0b1220',
  'circle-stroke-width': 3,
});

/** Popup rows (key order = display order). */
export function boreholePopupProps(p = {}) {
  const m = (v) => (v === null || v === undefined || v === '' ? 'n/a' : `${Number(Number(v).toFixed(2))} m`);
  return { borehole: p.name, elevation: m(p.elevation), total_depth: m(p.total_depth), lithology: p.lithology || 'none' };
}

// ── Sections ────────────────────────────────────────────────────────────────

/** The model's georeferenced line as near-line `line=` text, or null when not georeferenced. */
export function sectionLineParam(entry) {
  const line = entry?.line;
  if (!Array.isArray(line) || line.length < 2) return null;
  if (!line.every(p => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]))) return null;
  return line.map(([lon, lat]) => `${lon},${lat}`).join(';');
}

/** Search buffer either side of a section: 10 % of its length, 10-200 m. */
export function sectionBuffer(x0, x1) {
  return Math.max(10, Math.min(200, Math.abs(x1 - x0) * 0.1));
}

/**
 * Lithology column geometry in section (SVG) coordinates.
 *
 *   x          section x of the hole (model x0 + fraction * (x1 - x0))
 *   px, py     the section's scales: x -> svg x; plotted depth -> svg y
 *   topo       the section is drawn against a surface profile (elevation axis)
 *   surfaceMax highest surface elevation of the section (topo only)
 *   surfaceAt  x -> surface elevation (topo only)
 *   yTop/yBottom  plot box; rectangles are clipped to it
 *
 * Depths are below the ground at the hole: with topography the column starts at the
 * section's surface at x, i.e. plotted depth = (surfaceMax - surfaceAt(x)) + depth.
 */
export function columnGeometry({ x, intervals = [], px, py, topo = false, surfaceMax = 0, surfaceAt = null,
  yTop = -Infinity, yBottom = Infinity, width = 10, overrides = {} }) {
  const shift = topo && typeof surfaceAt === 'function' ? surfaceMax - surfaceAt(x) : 0;
  const cx = px(x);
  const rects = [];
  intervals.forEach((iv) => {
    const a = Math.max(yTop, py(shift + iv.depth_from));
    const b = Math.min(yBottom, py(shift + iv.depth_to));
    if (!(b > a)) return;
    rects.push({ y: a, h: b - a, color: lithologyColor(iv.lithology, iv.color, overrides),
      lithology: iv.lithology || '', depth_from: iv.depth_from, depth_to: iv.depth_to });
  });
  return { cx, left: cx - width / 2, width, top: Math.max(yTop, py(shift)), shift, rects };
}

/**
 * Log curve beside a column as an SVG polyline `points` string. Values are scaled to
 * [left, left + width] (log10 when every value is > 0 and they span over two decades).
 */
export function logPolyline(samples, { left, width = 24, depthToY, yTop = -Infinity, yBottom = Infinity }) {
  const d = samples?.depth || [];
  const v = samples?.value || [];
  const n = Math.min(d.length, v.length);
  const finite = v.slice(0, n).filter(Number.isFinite);
  if (!finite.length) return '';
  const lo = Math.min(...finite);
  const hi = Math.max(...finite);
  const useLog = lo > 0 && hi / lo > 100;
  const t = useLog ? Math.log10 : (x) => x;
  const tlo = t(lo);
  const span = (t(hi) - tlo) || 1;
  const step = Math.max(1, Math.floor(n / 400)); // at most ~400 vertices per curve
  const pts = [];
  for (let i = 0; i < n; i += step) {
    if (!Number.isFinite(v[i]) || !Number.isFinite(d[i])) continue;
    const y = depthToY(d[i]);
    if (y < yTop || y > yBottom) continue;
    pts.push(`${(left + ((t(v[i]) - tlo) / span) * width).toFixed(1)},${y.toFixed(1)}`);
  }
  return pts.join(' ');
}

// ── 3D ──────────────────────────────────────────────────────────────────────

const M_PER_DEG_LAT = 110540;

/** Shared 3D origin: the boreholes' centroid (when the scene has no other origin). */
export function boreholeOrigin(boreholes = []) {
  const pts = boreholes.filter(b => Number.isFinite(b.lon) && Number.isFinite(b.lat));
  if (!pts.length) return null;
  return {
    lon0: pts.reduce((s, b) => s + b.lon, 0) / pts.length,
    lat0: pts.reduce((s, b) => s + b.lat, 0) / pts.length,
  };
}

/**
 * One cylinder per interval in the SubsurfaceViewer3D frame (x = east, y = -depth,
 * z = north, metres from `origin`): [{ key, position: [x, y, z], height, color, ... }].
 */
export function boreholeCylinders(boreholes = [], origin, overrides = {}) {
  if (!origin) return [];
  const mLon = 111320 * Math.cos((origin.lat0 * Math.PI) / 180);
  const out = [];
  boreholes.forEach((b) => {
    if (!Number.isFinite(b.lon) || !Number.isFinite(b.lat)) return;
    const x = (b.lon - origin.lon0) * mLon;
    const z = (b.lat - origin.lat0) * M_PER_DEG_LAT;
    (b.intervals || []).forEach((iv, i) => {
      const h = iv.depth_to - iv.depth_from;
      if (!(h > 0)) return;
      out.push({ key: `${b.id}-${i}`, borehole: b.name, lithology: iv.lithology || '',
        position: [x, -(iv.depth_from + h / 2), z], height: h,
        color: lithologyColor(iv.lithology, iv.color, overrides) });
    });
  });
  return out;
}

/** bbox 'minLon,minLat,maxLon,maxLat' of a square `halfSize` metres around an origin. */
export function bboxAround(origin, halfSize = 5000) {
  const dLat = halfSize / M_PER_DEG_LAT;
  const dLon = halfSize / (111320 * Math.max(0.01, Math.cos((origin.lat0 * Math.PI) / 180)));
  return [origin.lon0 - dLon, origin.lat0 - dLat, origin.lon0 + dLon, origin.lat0 + dLat].map(v => v.toFixed(6)).join(',');
}
